import React, { useState, useEffect, useContext } from 'react';
import {
    ModalCard,
    SimpleCell,
    Spinner,
    Button,
    Spacing
} from '@vkontakte/vkui';
import { Icon24Cancel, Icon36CoinsStacks2Outline } from '@vkontakte/icons';
import { useRouteNavigator } from "@vkontakte/vk-mini-apps-router";
import { getCoinPackages } from '../../services/payment';
import { SnackbarContext } from '../../context/SnackbarContext';
import { useTelegramPayments } from '../../hooks/useTelegramPayments';
import {
    MODAL_CARD_CLASS,
    MODAL_HEADER_CLASS,
    MODAL_INNER_CARD_CLASS,
    MODAL_INNER_TEXT_CLASS,
    MODAL_PRIMARY_BUTTON_CLASS,
    MODAL_SECONDARY_BUTTON_CLASS,
    MODAL_SUBHEADER_CLASS
} from "../modalStyles";

/**
 * BuyCoinsModal - модальное окно для покупки игровых монет
 * Отображает доступные наборы монет и обрабатывает процесс покупки через Telegram Stars
 */
const BuyCoinsModal = ({ nav }) => {
    const routeNavigator = useRouteNavigator();
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { showSnackbar, showErrorSnackbar } = useContext(SnackbarContext);
    const { isProcessing, isPaymentSupported, initiatePurchase } = useTelegramPayments();

    // Загрузка наборов монет при открытии модального окна
    useEffect(() => {
        loadCoinPackages();
    }, []);

    const loadCoinPackages = () => {
        setLoading(true);
        setError(null);
        
        getCoinPackages()
            .then((response) => {
                setPackages(response.data.response);
            })
            .catch((err) => {
                console.error('Failed to load coin packages:', err);
                
                let errorMessage = 'Failed to load coin packages';
                
                if (err.response) {
                    // Ошибка от сервера
                    if (err.response.status >= 500) {
                        errorMessage = 'Temporary server issue. Please try again later.';
                    } else if (err.response.status >= 400) {
                        errorMessage = 'Invalid request. Please try again later.';
                    }
                } else if (err.request) {
                    // Проблемы с сетью
                    errorMessage = 'Network issue. Check your internet connection.';
                } else {
                    // Другие ошибки
                    errorMessage = err.message || 'Unknown error';
                }
                
                setError(errorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    };



    const handlePackageSelect = (pkg) => {
        setSelectedPackage(pkg);
    };

    const handlePurchase = () => {
        if (!selectedPackage) {
            showErrorSnackbar('Select a coin package');
            return;
        }

        if (!isPaymentSupported()) {
            showErrorSnackbar('Payments are not supported in this app version');
            return;
        }

        initiatePurchase(selectedPackage.item_id)
            .then((result) => {
                if (result.success) {
                    showSnackbar('Payment accepted. Coins will be credited in a few seconds.');
                    routeNavigator.hideModal();
                } else {
                    showSnackbar('Payment status is pending. Check your balance in a few minutes.');
                }
            })
            .catch((error) => {
                console.error('Purchase failed:', error);
                
                let errorMessage = 'Purchase failed. Please try again later.';
                
                if (error.response) {
                    // Ошибка от сервера
                    if (error.response.status === 402) {
                        errorMessage = 'Not enough Stars to complete this purchase';
                    } else if (error.response.status >= 500) {
                        errorMessage = 'Temporary server issue. Please try again later.';
                    } else if (error.response.data?.message) {
                        errorMessage = error.response.data.message;
                    }
                } else if (error.request) {
                    // Проблемы с сетью
                    errorMessage = 'Network issue. Check your internet connection.';
                } else {
                    // Другие ошибки
                    errorMessage = error.message || 'Purchase failed. Please try again later.';
                }
                
                showErrorSnackbar(errorMessage);
            });
    };

    if (loading) {
        return (
            <ModalCard
                nav={nav}
                onClose={() => routeNavigator.hideModal()}
                className={MODAL_CARD_CLASS}
                icon={<Spinner size="large" />}
                header={<span className={MODAL_HEADER_CLASS}>Loading...</span>}
                subheader={<span className={MODAL_SUBHEADER_CLASS}>Fetching available coin packages</span>}
            />
        );
    }

    if (error) {
        return (
            <ModalCard
                nav={nav}
                onClose={() => routeNavigator.hideModal()}
                className={MODAL_CARD_CLASS}
                icon={<Icon24Cancel className="text-red-400" />}
                header={<span className={MODAL_HEADER_CLASS}>Loading error</span>}
                subheader={<span className={MODAL_SUBHEADER_CLASS}>{error}</span>}
                actions={
                    <div className="flex flex-col w-full gap-2">
                        <Button
                            size="l"
                            mode="primary"
                            className={MODAL_PRIMARY_BUTTON_CLASS}
                            onClick={loadCoinPackages}
                        >
                            Retry
                        </Button>
                        <Button
                            size="l"
                            mode="secondary"
                            className={MODAL_SECONDARY_BUTTON_CLASS}
                            onClick={() => routeNavigator.hideModal()}
                        >
                            Close
                        </Button>
                    </div>
                }
            />
        );
    }

    if (packages.length === 0) {
        return (
            <ModalCard
                nav={nav}
                onClose={() => routeNavigator.hideModal()}
                className={MODAL_CARD_CLASS}
                icon={<Icon36CoinsStacks2Outline className="text-yellow-400" />}
                header={<span className={MODAL_HEADER_CLASS}>No packages available</span>}
                subheader={<span className={MODAL_SUBHEADER_CLASS}>Coin packages are temporarily unavailable. Please try again later.</span>}
                actions={
                    <Button
                        size="l"
                        mode="secondary"
                        className={MODAL_SECONDARY_BUTTON_CLASS}
                        onClick={() => routeNavigator.hideModal()}
                    >
                        Close
                    </Button>
                }
            />
        );
    }

    return (
        <ModalCard
            nav={nav}
            onClose={() => routeNavigator.hideModal()}
            className={MODAL_CARD_CLASS}
            icon={<Icon36CoinsStacks2Outline className="text-yellow-400" />}
            header={<span className={MODAL_HEADER_CLASS}>Buy coins</span>}
            subheader={<span className={MODAL_SUBHEADER_CLASS}>Choose a package to top up your balance</span>}
            actions={
                <div className="flex flex-col w-full gap-2">
                    {!isPaymentSupported() && (
                        <div className="text-sm text-center text-yellow-400 mb-2">
                            ⚠️ Payments are unavailable in this app version
                        </div>
                    )}

                    <div className={`w-full text-xs p-2 rounded-lg ${MODAL_INNER_CARD_CLASS}`}>
                        <span className={MODAL_INNER_TEXT_CLASS}>
                            Telegram Stars are used to buy digital goods in Mini Apps.
                        </span>
                    </div>
                    
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                        {packages.map((pkg) => (
                            <SimpleCell
                                key={pkg.id}
                                before={<Icon36CoinsStacks2Outline className="text-yellow-400" />}
                                after={<span className="text-gray-300">{`${pkg.price_stars} stars`}</span>}
                                onClick={() => handlePackageSelect(pkg)}
                                className={`cursor-pointer rounded-lg my-1 transition-all text-gray-100 ${
                                    selectedPackage?.id === pkg.id 
                                        ? 'bg-yellow-400/10 border-2 border-yellow-400/60' 
                                        : 'border border-gray-800 bg-gray-700 hover:bg-gray-600'
                                }`}
                            >
                                <div>
                                    <div className="font-bold text-gray-100">{pkg.name}</div>
                                    <div className="text-sm text-gray-400">
                                        {pkg.coins_amount} coins
                                    </div>
                                </div>
                            </SimpleCell>
                        ))}
                    </div>
                    
                    <Spacing size={8} />
                    
                    <Button
                        size="l"
                        mode="primary"
                        onClick={handlePurchase}
                        disabled={!selectedPackage || isProcessing || !isPaymentSupported()}
                        loading={isProcessing}
                        className={MODAL_PRIMARY_BUTTON_CLASS}
                    >
                                {!isPaymentSupported() 
                                    ? 'Payments unavailable'
                                    : isProcessing 
                                        ? 'Processing payment...'
                                        : selectedPackage 
                                    ? `Buy for ${selectedPackage.price_stars} stars` 
                                    : 'Select package'
                        }
                    </Button>
                    
                    <Button
                        size="l"
                        mode="secondary"
                        className={MODAL_SECONDARY_BUTTON_CLASS}
                        onClick={() => routeNavigator.hideModal()}
                    >
                        Cancel
                    </Button>
                </div>
            }
        />
    );
};

export default BuyCoinsModal;
